import { DeviceToolError, toDeviceToolError } from "../errors";
import { readFileChunks } from "../files/ChunkedFileReader";
import type { DeviceInfo, OperationProgress, UsbProtocolService } from "../types";
import { WebUsbTransport } from "../usb/WebUsbTransport";
import { FASTBOOT_SPARSE_SPLIT_SIZE, inspectSparseImage, splitFastbootImage } from "./SparseImage";

export interface FastbootResponse {
  status: "INFO" | "OKAY" | "FAIL" | "DATA" | "TEXT";
  payload: string;
}

export interface FastbootVariables {
  product?: string;
  serialno?: string;
  "version-bootloader"?: string;
  "current-slot"?: string;
  "slot-count"?: string;
  "is-userspace"?: string;
  "max-download-size"?: string;
  unlocked?: string;
  secure?: string;
  [key: string]: string | undefined;
}

const decoder = new TextDecoder();
const encoder = new TextEncoder();

function parseResponse(bytes: Uint8Array): FastbootResponse {
  const text = decoder.decode(bytes);
  const status = text.slice(0, 4);
  if (!["INFO", "OKAY", "FAIL", "DATA", "TEXT"].includes(status)) throw new DeviceToolError("FASTBOOT_COMMAND_FAILED", `Response không hợp lệ: ${status}`);
  return { status: status as FastbootResponse["status"], payload: text.slice(4) };
}

function parseSize(value?: string): number | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  // Fastboot getvar values are hexadecimal even when the 0x prefix is
  // omitted (for example, max-download-size: 08000000).
  const number = Number.parseInt(normalized.replace(/^0x/, ""), 16);
  return Number.isFinite(number) && number > 0 ? number : undefined;
}

export class FastbootService implements UsbProtocolService {
  readonly transport = new WebUsbTransport("FASTBOOT");
  private controller?: AbortController;
  private variables: FastbootVariables = {};

  async connect(): Promise<void> {
    await this.transport.connect();
  }

  async connectAuthorizedDevice(device: USBDevice): Promise<void> {
    await this.transport.connectAuthorizedDevice(device);
  }

  async disconnect(): Promise<void> {
    this.controller?.abort();
    await this.transport.disconnect();
    this.variables = {};
  }

  isConnected(): boolean {
    return this.transport.isConnected();
  }

  async getDeviceInfo(): Promise<DeviceInfo> {
    return this.transport.getDeviceInfo();
  }

  private async readResponse(signal?: AbortSignal): Promise<FastbootResponse> {
    const bytes = await this.transport.transferIn(4096, { timeoutMs: 15_000, signal });
    return parseResponse(bytes);
  }

  async command(command: string, signal?: AbortSignal): Promise<FastbootResponse[]> {
    if (!this.isConnected()) throw new DeviceToolError("DEVICE_DISCONNECTED");
    const operationController = new AbortController();
    this.controller = operationController;
    const combinedSignal = signal ?? operationController.signal;
    const responses: FastbootResponse[] = [];
    try {
      await this.transport.transferOut(encoder.encode(command), { signal: combinedSignal });
      for (;;) {
        const response = await this.readResponse(combinedSignal);
        responses.push(response);
        if (response.status === "INFO" || response.status === "TEXT") continue;
        if (response.status === "FAIL") throw new DeviceToolError("FASTBOOT_COMMAND_FAILED", response.payload);
        return responses;
      }
    } catch (error) {
      if (error instanceof DeviceToolError) throw error;
      throw toDeviceToolError(error, "FASTBOOT_COMMAND_FAILED");
    } finally {
      this.controller = undefined;
    }
  }

  async getvar(name: string): Promise<FastbootResponse[]> {
    const responses = await this.command(`getvar:${name}`);
    const last = responses[responses.length - 1];
    if (last?.status === "OKAY") this.variables[name] = last.payload;
    return responses;
  }

  async getvarAll(): Promise<FastbootVariables> {
    const responses = await this.command("getvar:all");
    responses.forEach((response) => {
      const match = response.payload.match(/^([^:]+):\s*(.*)$/);
      if (match) this.variables[match[1]] = match[2];
    });
    return { ...this.variables };
  }

  private async resolveMaxDownloadSize(): Promise<number> {
    const cached = parseSize(this.variables["max-download-size"]);
    if (cached) return cached;
    try {
      await this.getvar("max-download-size");
    } catch {
      // Use the requested -S 128M fallback when an older bootloader does not
      // expose max-download-size. The device still validates each download.
    }
    return parseSize(this.variables["max-download-size"]) ?? FASTBOOT_SPARSE_SPLIT_SIZE;
  }

  async download(file: Blob, onProgress?: (progress: OperationProgress) => void, signal?: AbortSignal): Promise<void> {
    const maxDownloadSize = parseSize(this.variables["max-download-size"]);
    if (maxDownloadSize && file.size > maxDownloadSize) throw new DeviceToolError("UNSUPPORTED_IMAGE", `Image lớn hơn max-download-size (${maxDownloadSize} bytes).`);
    const request = await this.command(`download:${file.size.toString(16).padStart(8, "0")}`, signal);
    const dataResponse = request[request.length - 1];
    if (dataResponse?.status !== "DATA") throw new DeviceToolError("FASTBOOT_COMMAND_FAILED", "Thiết bị không chấp nhận download.");
    const startedAt = Date.now();
    let sent = 0;
    const chunkSize = Math.min(maxDownloadSize ?? 1024 * 1024, 1024 * 1024);
    for await (const chunk of readFileChunks(file, chunkSize, signal)) {
      sent += await this.transport.transferOut(chunk, { signal });
      onProgress?.({ phase: "Đang truyền", completedBytes: sent, totalBytes: file.size, startedAt, speedBytesPerSecond: sent / Math.max(0.001, (Date.now() - startedAt) / 1000) });
    }
    const response = await this.readResponse(signal);
    if (response.status === "FAIL") throw new DeviceToolError("FASTBOOT_COMMAND_FAILED", response.payload);
    if (response.status !== "OKAY") throw new DeviceToolError("FASTBOOT_COMMAND_FAILED", `Kết thúc download: ${response.status}`);
  }

  async flash(partition: string, file: File, onProgress?: (progress: OperationProgress) => void, signal?: AbortSignal): Promise<FastbootResponse[]> {
    const sparse = await inspectSparseImage(file);
    const maxDownloadSize = await this.resolveMaxDownloadSize();
    const splitSize = Math.min(FASTBOOT_SPARSE_SPLIT_SIZE, maxDownloadSize);
    const totalBytes = sparse.header?.outputSize ?? file.size;

    if (file.size <= splitSize) {
      await this.download(file, onProgress, signal);
      return this.command(`flash:${partition}`, signal);
    }

    const startedAt = Date.now();
    let completedBytes = 0;
    let lastResponse: FastbootResponse[] = [];
    let splitIndex = 0;
    for await (const part of splitFastbootImage(file, splitSize)) {
      splitIndex += 1;
      await this.download(part.blob, (progress) => {
        const partTotal = progress.totalBytes ?? part.blob.size;
        const ratio = partTotal > 0 ? progress.completedBytes / partTotal : 0;
        onProgress?.({
          phase: `Download sparse [${splitIndex}]`,
          completedBytes: Math.min(totalBytes, completedBytes + ratio * part.outputBytes),
          totalBytes,
          startedAt,
          speedBytesPerSecond: (completedBytes + ratio * part.outputBytes) / Math.max(0.001, (Date.now() - startedAt) / 1000),
        });
      }, signal);
      lastResponse = await this.command(`flash:${partition}`, signal);
      completedBytes = Math.min(totalBytes, completedBytes + part.outputBytes);
      onProgress?.({
        phase: `Flash sparse [${splitIndex}]`,
        completedBytes,
        totalBytes,
        startedAt,
        speedBytesPerSecond: completedBytes / Math.max(0.001, (Date.now() - startedAt) / 1000),
      });
    }
    if (!splitIndex) throw new DeviceToolError("UNSUPPORTED_IMAGE", "Không tạo được sparse payload để flash.");
    return lastResponse;
  }

  async boot(file: File, onProgress?: (progress: OperationProgress) => void, signal?: AbortSignal): Promise<FastbootResponse[]> {
    await this.download(file, onProgress, signal);
    return this.command("boot", signal);
  }

  async erase(partition: string): Promise<FastbootResponse[]> { return this.command(`erase:${partition}`); }
  /** Implements the host-side `fastboot -w` wipe semantics. */
  async wipeData(): Promise<void> {
    await this.erase("userdata");
    let cacheType = this.variables["partition-type:cache"];
    if (cacheType === undefined) {
      try {
        await this.getvar("partition-type:cache");
        cacheType = this.variables["partition-type:cache"];
      } catch {
        cacheType = undefined;
      }
    }
    if (cacheType) await this.erase("cache");
  }
  async setActive(slot: "a" | "b"): Promise<FastbootResponse[]> { return this.command(`set_active:${slot}`); }
  async reboot(mode = ""): Promise<FastbootResponse[]> { return this.command(`reboot${mode ? `-${mode}` : ""}`); }
  async cancelCurrentOperation(): Promise<void> { this.controller?.abort(); await this.transport.cancelCurrentOperation(); }
  get cachedVariables(): FastbootVariables { return { ...this.variables }; }
}
